import { useEffect, useState } from "react";
import { Box, MenuItem, TextField, Typography, useTheme } from "@mui/material";
import { listDroneCatalog } from "./api/droneCatalog";
import { SOURCES } from "./droneCatalog";

const SOURCE_LABEL = {
  fleet: "Fleet",
  devices: "Registered devices",
  propulsion: "Builds",
};

/**
 * Associate a log with a drone from the catalog — optional, and the aircraft's
 * details are none of this page's business beyond the name.
 *
 * Grouped by registry rather than flattened: picking "this airframe" and
 * picking "this build" are different claims, and a flat list would hide which
 * one is being made.
 */
const DronePicker = ({ value, onChange }) => {
  const theme = useTheme();
  const [catalog, setCatalog] = useState([]);
  const [unavailable, setUnavailable] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let live = true;
    listDroneCatalog()
      .then(({ entries, unavailable: missing }) => {
        if (!live) return;
        setCatalog(entries);
        setUnavailable(missing);
      })
      .catch(() => live && setUnavailable(SOURCES));
    return () => {
      live = false;
    };
  }, []);

  const select = async (ref) => {
    setSaving(true);
    setError(null);
    try {
      await onChange(ref || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 560 }}>
      <TextField
        select
        fullWidth
        size="small"
        value={value || ""}
        disabled={saving}
        onChange={(e) => select(e.target.value)}
        SelectProps={{ displayEmpty: true }}
      >
        <MenuItem value="">
          <em>Not associated</em>
        </MenuItem>
        {SOURCES.flatMap((source) => {
          const group = catalog.filter((entry) => entry.source === source);
          if (group.length === 0) return [];
          return [
            <MenuItem key={source} disabled sx={{ opacity: 0.7, fontSize: 12, letterSpacing: 0.4 }}>
              {SOURCE_LABEL[source].toUpperCase()}
            </MenuItem>,
            ...group.map((entry) => (
              <MenuItem key={entry.ref} value={entry.ref}>
                {entry.name}
                {entry.detail ? ` — ${entry.detail}` : ""}
              </MenuItem>
            )),
          ];
        })}
      </TextField>

      {error && (
        <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.75 }}>
          {error}
        </Typography>
      )}
      {unavailable.length > 0 && (
        <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: theme.palette.text.secondary }}>
          {unavailable.map((s) => SOURCE_LABEL[s]).join(" and ")} could not be loaded — anything registered
          there is missing from this list.
        </Typography>
      )}
    </Box>
  );
};

export default DronePicker;
